import { supabase } from '../lib/supabase';

const isSchemaError = (error) => {
  if (!error) return false;
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  return false;
};

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

// Retry helper: retries fn up to maxRetries times with exponential backoff
const withRetry = async (fn, maxRetries = 2, label = '') => {
  let lastError;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      // Do not retry on timeout errors — they indicate DB overload, retrying won't help
      const isTimeout = err?.message?.includes('Tiempo de espera') || err?.message?.includes('timeout') || err?.message?.toLowerCase()?.includes('timed out');
      if (isTimeout) {
        console.warn(`[inspectionService] ${label} timed out, not retrying.`);
        throw err;
      }
      if (attempt < maxRetries) {
        const delay = 500 * Math.pow(2, attempt);
        console.warn(`[inspectionService] ${label} attempt ${attempt + 1} failed, retrying in ${delay}ms:`, err?.message);
        await new Promise(r => setTimeout(r, delay));
      }
    }
  }
  throw lastError;
};

// Bad-condition statuses that trigger quote sync and outbox
const BAD_STATUSES = new Set(['malo', 'critico', 'requiere_atencion']);

export const inspectionService = {
  async getByWorkOrder(workOrderId) {
    try {
      const { data, error } = await supabase?.from('technical_inspections')?.select('*')?.eq('work_order_id', workOrderId)?.order('created_at', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching inspections:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch inspections:', error);
      throw error;
    }
  },

  async getByReceptionId(receptionId) {
    try {
      const { data, error } = await supabase
        ?.from('technical_inspections')
        ?.select('*')
        ?.eq('reception_id', receptionId)
        ?.order('created_at', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching inspections by reception_id:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch inspections by reception_id:', error);
      throw error;
    }
  },

  async upsert(inspectionData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Usuario no autenticado');

      const snakeData = toSnakeCase({
        ...inspectionData,
        inspectedBy: user?.id
      });

      let data, error;
      if (snakeData?.id) {
        const result = await supabase?.from('technical_inspections')?.update(snakeData)?.eq('id', snakeData?.id)?.select()?.single();
        data = result?.data;
        error = result?.error;
      } else {
        const result = await supabase?.from('technical_inspections')?.insert(snakeData)?.select()?.single();
        data = result?.data;
        error = result?.error;
      }

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        throw error;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to upsert inspection:', error);
      throw error;
    }
  },

  async saveBatchInspections(workOrderId, inspectionDataByCategory, vehicleId = null, clientId = null, receptionId = null) {
    return withRetry(async () => {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Usuario no autenticado');

      // Resolve vehicleId from receptions or work_orders if not passed
      let resolvedVehicleId = vehicleId;
      let resolvedClientId = clientId;

      if (!resolvedVehicleId && receptionId) {
        try {
          const { data: rec } = await supabase
            ?.from('receptions')
            ?.select('vehicle_id, client_id')
            ?.eq('id', receptionId)
            ?.maybeSingle();
          resolvedVehicleId = rec?.vehicle_id || null;
          if (!resolvedClientId) resolvedClientId = rec?.client_id || null;
        } catch {}
      }

      if (!resolvedVehicleId && workOrderId) {
        try {
          const { data: wo } = await supabase
            ?.from('work_orders')
            ?.select('vehicle_id, client_id')
            ?.eq('id', workOrderId)
            ?.maybeSingle();
          resolvedVehicleId = wo?.vehicle_id || null;
          if (!resolvedClientId) resolvedClientId = wo?.client_id || null;
        } catch {}
      }

      const statusMap = {
        'bueno': 'bueno',
        'regular': 'requiere_atencion',
        'malo': 'requiere_atencion',
        'critico': 'critico',
        'requiere_atencion': 'requiere_atencion',
        'no_aplica': 'no_aplica'
      };

      const inspectionRecords = Object.entries(inspectionDataByCategory)?.map(([category, data]) => ({
        reception_id: receptionId || null,
        work_order_id: workOrderId || null,
        vehicle_id: resolvedVehicleId,
        client_id: resolvedClientId || null,
        category,
        status: statusMap?.[data?.status] || 'no_aplica',
        findings: data?.findings || null,
        recommendations: data?.recommendations || null,
        photos: data?.photos || [],
        refaccion: data?.partsRequired?.[0]?.description || data?.refaccion || null,
        cantidad: data?.partsRequired?.[0]?.quantity || data?.cantidad || 1,
        requiere_mano_obra: data?.requiereManoObra || false,
        horas_estimadas: data?.horasEstimadas || null,
        inspected_by: user?.id,
        inspected_at: new Date()?.toISOString()
      }));

      let data, error;

      if (receptionId) {
        // Primary path: upsert by reception_id + category
        const result = await supabase
          ?.from('technical_inspections')
          ?.upsert(inspectionRecords, { onConflict: 'reception_id,category' })
          ?.select();
        data = result?.data;
        error = result?.error;

        // If upsert by reception_id,category fails (constraint not yet in DB), fall back to insert
        if (error && (error?.code === '42P10' || error?.message?.includes('there is no unique or exclusion constraint'))) {
          console.warn('[inspectionService] reception_id,category upsert constraint not found, falling back to insert');
          // Delete existing and re-insert
          await supabase
            ?.from('technical_inspections')
            ?.delete()
            ?.eq('reception_id', receptionId);
          const insertResult = await supabase
            ?.from('technical_inspections')
            ?.insert(inspectionRecords)
            ?.select();
          data = insertResult?.data;
          error = insertResult?.error;
        }
      } else if (workOrderId) {
        const result = await supabase
          ?.from('technical_inspections')
          ?.upsert(inspectionRecords, { onConflict: 'work_order_id,category' })
          ?.select();
        data = result?.data;
        error = result?.error;
      } else if (resolvedVehicleId) {
        const categories = Object.keys(inspectionDataByCategory);
        await supabase
          ?.from('technical_inspections')
          ?.delete()
          ?.eq('vehicle_id', resolvedVehicleId)
          ?.is('work_order_id', null)
          ?.is('reception_id', null)
          ?.in('category', categories);

        const result = await supabase
          ?.from('technical_inspections')
          ?.insert(inspectionRecords)
          ?.select();
        data = result?.data;
        error = result?.error;
      } else {
        const result = await supabase
          ?.from('technical_inspections')
          ?.insert(inspectionRecords)
          ?.select();
        data = result?.data;
        error = result?.error;
      }

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }

      // Save multi-row parts to inspection_parts_required
      try {
        const savedRecords = data || [];
        const partsRows = [];
        savedRecords?.forEach(record => {
          const catData = inspectionDataByCategory?.[record?.category];
          const partsRequired = Array.isArray(catData?.partsRequired) ? catData?.partsRequired : [];
          partsRequired?.forEach(part => {
            if (!part?.description) return;
            partsRows?.push({
              inspection_id: record?.id,
              work_order_id: workOrderId || null,
              category: record?.category,
              part_description: part?.description,
              quantity: part?.quantity || 1
            });
          });
        });

        if (partsRows?.length > 0) {
          const inspectionIds = savedRecords?.map(r => r?.id)?.filter(Boolean);
          if (inspectionIds?.length > 0) {
            await supabase
              ?.from('inspection_parts_required')
              ?.delete()
              ?.in('inspection_id', inspectionIds);
          }
          await supabase
            ?.from('inspection_parts_required')
            ?.insert(partsRows);
        }
      } catch (partsErr) {
        console.warn('[inspectionService] inspection_parts_required save failed (non-blocking):', partsErr?.message);
      }

      return toCamelCase(data || []);
    }, 2, 'saveBatchInspections');
  },

  // Sync bad-condition findings to quotation items (idempotent)
  async syncInspectionToQuote(otId, inspectionDataByCategory, vehicleId, clientId, companyId) {
    try {
      if (!otId && !vehicleId) return null;

      // Find or create a draft quotation for this OT
      let quotationId = null;
      if (otId) {
        const { data: existingQuote } = await supabase
          ?.from('quotations')
          ?.select('id')
          ?.eq('work_order_id', otId)
          ?.maybeSingle();
        quotationId = existingQuote?.id || null;

        if (!quotationId) {
          const { data: { user } } = await supabase?.auth?.getUser();
          const { data: newQuote, error: qErr } = await supabase
            ?.from('quotations')
            ?.insert({
              work_order_id: otId,
              client_id: clientId || null,
              vehicle_id: vehicleId || null,
              company_id: companyId || null,
              status: 'borrador',
              created_by: user?.id || null,
              items: [],
              total: 0
            })
            ?.select('id')
            ?.single();
          if (qErr) {
            console.warn('[inspectionService] Could not create draft quotation:', qErr?.message);
            return null;
          }
          quotationId = newQuote?.id;
        }
      }

      if (!quotationId) return null;

      // Build partidas from bad-condition findings
      const categoryLabels = {
        frenos: 'Frenos', suspension: 'Suspensión', motor: 'Motor/Afinación',
        otros: 'Otros Sistemas', llantas: 'Llantas'
      };

      const partidas = [];
      Object.entries(inspectionDataByCategory || {})?.forEach(([cat, catData]) => {
        const isBad = BAD_STATUSES?.has(catData?.status) ||
          catData?.status === 'malo' || catData?.status === 'critico';
        if (!isBad) return;

        const catLabel = categoryLabels?.[cat] || cat;

        // Multi-row partsRequired takes priority
        const partsRequired = Array.isArray(catData?.partsRequired) ? catData?.partsRequired : [];
        if (partsRequired?.length > 0) {
          partsRequired?.forEach(part => {
            if (!part?.description) return;
            partidas?.push({
              type: 'refaccion',
              description: part?.description,
              category: catLabel,
              quantity: part?.quantity || 1,
              price: 0,
              notes: catData?.findings || '',
              source_category: cat
            });
          });
        } else if (catData?.refaccion) {
          partidas?.push({
            type: 'refaccion',
            description: catData?.refaccion,
            category: catLabel,
            quantity: catData?.cantidad || 1,
            price: 0,
            notes: catData?.findings || '',
            source_category: cat
          });
        } else if (catData?.findings) {
          partidas?.push({
            type: 'refaccion',
            description: `[${catLabel}] ${catData?.findings?.substring(0, 100)}`,
            category: catLabel,
            quantity: catData?.cantidad || 1,
            price: 0,
            notes: catData?.recommendations || '',
            source_category: cat
          });
        }

        // Mano de obra partida
        if (catData?.requiereManoObra || catData?.horasEstimadas) {
          partidas?.push({
            type: 'mano_obra',
            description: `Mano de obra — ${catLabel}`,
            category: catLabel,
            quantity: catData?.horasEstimadas || 1,
            price: 0,
            notes: `Horas estimadas: ${catData?.horasEstimadas || 1}`,
            source_category: cat
          });
        }

        // Parts from template
        (catData?.parts || [])?.forEach(part => {
          if (!part?.name) return;
          // Skip if already in partsRequired
          const alreadyInRequired = partsRequired?.some(p => p?.description?.toLowerCase() === part?.name?.toLowerCase());
          if (alreadyInRequired) return;
          partidas?.push({
            type: 'refaccion',
            description: part?.name,
            category: catLabel,
            quantity: part?.qty || 1,
            price: 0,
            notes: part?.notes || '',
            source_category: cat
          });
        });
      });

      if (partidas?.length === 0) return quotationId;

      // Fetch existing items to avoid duplicates
      const { data: existingItems } = await supabase
        ?.from('quotation_lines')
        ?.select('item_name, category')
        ?.eq('quotation_id', quotationId);

      const existingSet = new Set(
        (existingItems || [])?.map(i => `${i?.category}::${i?.item_name}`)
      );

      const newLines = partidas
        ?.filter(p => !existingSet?.has(`${p?.category}::${p?.description}`))
        ?.map(p => ({
          quotation_id: quotationId,
          company_id: companyId || null,
          category: p?.category,
          item_name: p?.description,
          qty: p?.quantity || 1,
          unit_price: 0,
          notes: p?.notes || null
        }));

      if (newLines?.length > 0) {
        const { error: linesErr } = await supabase
          ?.from('quotation_lines')
          ?.insert(newLines);
        if (linesErr) {
          console.warn('[inspectionService] syncInspectionToQuote lines error:', linesErr?.message);
        }
      }

      return quotationId;
    } catch (err) {
      console.error('[inspectionService] syncInspectionToQuote failed:', err);
      return null;
    }
  },

  // Upsert outbox_supplier_requests for bad-condition findings
  async upsertOutboxSupplierRequest({
    otId, vehicleId, clientId, companyId, vehicleData, inspectionDataByCategory
  }) {
    try {
      const items = [];
      const badStatuses = new Set(['malo', 'critico', 'requiere_atencion']);
      const categoryLabels = {
        frenos: 'Frenos', suspension: 'Suspensión', motor: 'Motor/Afinación',
        otros: 'Otros Sistemas', llantas: 'Llantas'
      };

      Object.entries(inspectionDataByCategory || {})?.forEach(([cat, catData]) => {
        if (!badStatuses?.has(catData?.status) && catData?.status !== 'malo' && catData?.status !== 'critico') return;

        const catLabel = categoryLabels?.[cat] || cat;

        // Priority 1: partsRequired (multi-row from InspectionCategoryPanel)
        const partsRequired = Array.isArray(catData?.partsRequired) ? catData?.partsRequired : [];
        if (partsRequired?.length > 0) {
          partsRequired?.forEach(part => {
            if (!part?.description) return;
            items?.push({
              rubro: catLabel,
              refaccion: part?.description,
              qty: part?.quantity || 1,
              notas: catData?.recommendations || ''
            });
          });
        } else if (catData?.refaccion) {
          // Priority 2: single refaccion field
          items?.push({
            rubro: catLabel,
            refaccion: catData?.refaccion,
            qty: catData?.cantidad || 1,
            notas: catData?.recommendations || ''
          });
        } else if (catData?.findings) {
          // Priority 3: findings text as fallback
          items?.push({
            rubro: catLabel,
            refaccion: catData?.findings?.substring(0, 80),
            qty: 1,
            notas: catData?.recommendations || ''
          });
        }

        // Also include template parts (catData.parts)
        (catData?.parts || [])?.forEach(part => {
          if (!part?.name) return;
          // Avoid duplicates with partsRequired
          const alreadyIn = items?.some(i => i?.refaccion?.toLowerCase() === part?.name?.toLowerCase());
          if (alreadyIn) return;
          items?.push({
            rubro: catLabel,
            refaccion: part?.name,
            qty: part?.qty || 1,
            notas: part?.notes || ''
          });
        });

        // Add labor if required
        if (catData?.requiereManoObra || catData?.horasEstimadas) {
          items?.push({
            rubro: catLabel,
            refaccion: `Mano de obra — ${catLabel}`,
            qty: catData?.horasEstimadas || 1,
            notas: `Horas estimadas: ${catData?.horasEstimadas || 1}`,
            tipo: 'mano_obra'
          });
        }
      });

      if (items?.length === 0) return null;

      // Check if pending outbox already exists for this ot+vehicle
      let existingId = null;
      if (otId && vehicleId) {
        const { data: existing } = await supabase
          ?.from('outbox_supplier_requests')
          ?.select('id, items')
          ?.eq('ot_id', otId)
          ?.eq('vehicle_id', vehicleId)
          ?.eq('status', 'pending')
          ?.maybeSingle();
        existingId = existing?.id || null;
      }

      const payload = {
        company_id: companyId || null,
        ot_id: otId || null,
        vehicle_id: vehicleId || null,
        client_id: clientId || null,
        vin: vehicleData?.vin || null,
        marca: vehicleData?.marca || vehicleData?.make || null,
        modelo: vehicleData?.modelo || vehicleData?.model || null,
        anio: vehicleData?.year ? String(vehicleData?.year) : null,
        placas: vehicleData?.placas || vehicleData?.plates || null,
        items,
        status: 'pending',
        updated_at: new Date()?.toISOString()
      };

      if (existingId) {
        await supabase
          ?.from('outbox_supplier_requests')
          ?.update(payload)
          ?.eq('id', existingId);
        return existingId;
      } else {
        const { data: created } = await supabase
          ?.from('outbox_supplier_requests')
          ?.insert(payload)
          ?.select('id')
          ?.single();
        return created?.id || null;
      }
    } catch (err) {
      console.error('[inspectionService] upsertOutboxSupplierRequest failed:', err);
      return null;
    }
  },

  // Save appointment for a rubro from recommendations
  async saveAppointment({ companyId, clientId, vehicleId, otId, rubro, fechaHora, motivo, notas }) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const { data, error } = await supabase
        ?.from('appointments')
        ?.insert({
          company_id: companyId || null,
          client_id: clientId || null,
          vehicle_id: vehicleId || null,
          ot_id: otId || null,
          rubro: rubro || 'general',
          fecha_hora: fechaHora,
          motivo: motivo || `Revisión de ${rubro}`,
          notas: notas || null,
          estado: 'programado',
          origen: 'inspection',
          created_by: user?.id || null
        })
        ?.select()
        ?.single();

      if (error) throw error;

      // Also create a calendar_events entry so it shows in Calendario
      try {
        await supabase?.from('calendar_events')?.insert({
          company_id: companyId || null,
          title: motivo || `Revisión de ${rubro}`,
          description: notas || `Próxima revisión de ${rubro} agendada desde inspección`,
          related_type: 'inspection',
          related_id: data?.id,
          client_id: clientId || null,
          vehicle_id: vehicleId || null,
          start_datetime: fechaHora,
          end_datetime: new Date(new Date(fechaHora)?.getTime() + 60 * 60 * 1000)?.toISOString(),
          status: 'scheduled',
          color: '#8B5CF6',
          created_by: user?.id || null
        });
      } catch (calErr) {
        console.warn('[inspectionService] calendar_events insert failed (non-blocking):', calErr?.message);
      }

      return toCamelCase(data);
    } catch (err) {
      console.error('[inspectionService] saveAppointment failed:', err);
      throw err;
    }
  }
};